import { useSearchExperience } from "hooks/useSearchExperience";
import { UserUseCase } from "lib/models/userusecase-models";
import { CatalogicalSearchRequestContent } from "lib/services/shop-search-service";
import { useCallback, useEffect, useRef } from "react";

import { USE_CASE_SORT_OPTIONS, USE_CASES_API_PAGE_SIZE, USE_CASES_SEARCH_DEBOUNCE_MS } from "../constants";

interface UseUseCaseBrowseParams {
  searchQuery: string;
  selectedCategories: string[];
  selectedJobFamilies: string[];
  sortBy: string;
}

export function useUseCaseBrowse(params: UseUseCaseBrowseParams) {
  const { items, isLoading, isLoadingMore, hasMoreItems, totalCount, searchCatalogItems, searchNext, nextToken } =
    useSearchExperience<UserUseCase>("userusecase");

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const isInitializedRef = useRef(false);

  const buildRequest = useCallback(
    (offset: number): CatalogicalSearchRequestContent => {
      const filters: Record<string, any> = {
        status: ["PUBLISHED"],
        ...(params.selectedCategories.length && { categories: params.selectedCategories }),
        ...(params.selectedJobFamilies.length && { recommendedAudience: params.selectedJobFamilies }),
      };

      const sortOption = USE_CASE_SORT_OPTIONS.find((o) => o.label === params.sortBy);

      return {
        searchQuery: params.searchQuery || undefined,
        useSemanticSearch: !!params.searchQuery,
        filter: JSON.stringify(filters),
        sortBy: sortOption?.field,
        sortDirection: sortOption?.direction,
        limit: USE_CASES_API_PAGE_SIZE,
        offset,
        excludeFields: "embeddings",
      };
    },
    [params.searchQuery, params.selectedCategories, params.selectedJobFamilies, params.sortBy]
  );

  const loadMore = useCallback(async () => {
    if (!isLoading && !isLoadingMore && hasMoreItems) {
      await searchNext(buildRequest(nextToken));
    }
  }, [isLoading, isLoadingMore, hasMoreItems, nextToken, buildRequest, searchNext]);

  // Trigger search when params change (debounced)
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const currentRequestId = ++requestIdRef.current;
    const delay = isInitializedRef.current ? USE_CASES_SEARCH_DEBOUNCE_MS : 0;

    timeoutRef.current = setTimeout(() => {
      if (currentRequestId === requestIdRef.current) {
        searchCatalogItems(buildRequest(0));
        isInitializedRef.current = true;
      }
    }, delay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.searchQuery, params.selectedCategories, params.selectedJobFamilies, params.sortBy]);

  return { items, isLoading, isLoadingMore, hasMoreItems, totalCount, loadMore };
}
